import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Toolbar,
  AppBar,
  Typography,
  CssBaseline,
  Button,
  LinearProgress,
  IconButton,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  People as PeopleIcon,
  School as SchoolIcon,
  Groups as GroupsIcon,
  FolderShared as FolderSharedIcon,
  Assignment as AssignmentIcon,
  Quiz as QuizIcon,
  Description as DescriptionIcon,
  Notifications as NotificationsIcon,
  Inbox as InboxIcon,
  EmojiEvents as EmojiEventsIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Dashboard as DashboardIcon,
  AccessTime as AccessTimeIcon,
  Videocam as VideocamIcon,
  VideoLibrary as VideoLibraryIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Logout, AccountCircle } from "@mui/icons-material";
import TesterPortalSwitcher from "../../components/TesterPortalSwitcher";
import api from "../../api";
import ParentWelcomeTour from "../../components/ParentWelcomeTour";
import AssistantDrawer from "../../components/PortalAssistant/AssistantDrawer";

const drawerWidth = 240;

const tutorDrawerPaperSx = (width, appBarHeight) => ({
  width,
  boxSizing: "border-box",
  mt: `${appBarHeight}px`,
  maxHeight: `calc(100vh - ${appBarHeight}px)`,
  // dvh so iOS Safari's bottom toolbar doesn't cover the end of the menu.
  "@supports (height: 100dvh)": { maxHeight: `calc(100dvh - ${appBarHeight}px)` },
  background: "linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)",
  borderRight: "1px solid #dee2e6",
  overflowY: "auto",
  overflowX: "hidden",
  transition: "all 0.3s ease",
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-track": {
    background: "rgba(0,0,0,0.05)",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.2)",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "linear-gradient(180deg, #3B2A9F 0%, #D62926 100%)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: "#667eea rgba(0,0,0,0.05)",
});

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("userData") || "null");
  } catch {
    return null;
  }
};

const TutorLayout = () => {
  const tourUser = readStoredUser();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isCompactAppBar = useMediaQuery(theme.breakpoints.down("sm"));
  const appBarHeight = isCompactAppBar ? 56 : 70;
  const loading = false;
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tutorName, setTutorName] = useState("");
  const [tutorEmail, setTutorEmail] = useState("");
  const [announcementCount, setAnnouncementCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [tutorPortalPermissions, setTutorPortalPermissions] = useState(() => {
    try {
      const raw = localStorage.getItem("tutor-permissions");
      if (raw === null) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [tutorPermissionsSynced, setTutorPermissionsSynced] = useState(
    () => localStorage.getItem("tutor-permissions") !== null
  );

  const menuItems = useMemo(
    () => [
      { label: "Dashboard", path: "/tutor", icon: <DashboardIcon />, permission: "tutor-portal-view-dashboard" },
      { label: "Students", path: "/tutor/students", icon: <PeopleIcon />, permission: "tutor-portal-students" },
      { label: "Courses", path: "/tutor/courses", icon: <SchoolIcon />, permission: "tutor-portal-courses" },
      { label: "Groups", path: "/tutor/groups", icon: <GroupsIcon />, permission: "tutor-portal-groups" },
      { label: "My Content", path: "/tutor/resources", icon: <FolderSharedIcon />, permission: "tutor-portal-resources" },
      { label: "Classes", path: "/tutor/classes", icon: <VideocamIcon />, permission: "tutor-portal-classes" },
      { label: "Recordings", path: "/tutor/recordings", icon: <VideoLibraryIcon />, permission: "tutor-portal-classes" },
      { label: "Mock Exams", path: "/tutor/mock-exams", icon: <QuizIcon />, permission: "tutor-portal-mock-exams" },
      { label: "Papers", path: "/tutor/papers", icon: <DescriptionIcon />, permission: "tutor-portal-papers" },
      { label: "Assignments", path: "/tutor/assignments", icon: <AssignmentIcon />, permission: "tutor-portal-assignments" },
      { label: "Worksheets", path: "/tutor/tests", icon: <QuizIcon />, permission: "tutor-portal-tests" },
      { label: "Timeslots", path: "/tutor/timeslots", icon: <AccessTimeIcon />, permission: "tutor-portal-timeslots" },
      { label: "Notifications", path: "/tutor/notifications", icon: <InboxIcon />, permission: "tutor-portal-view-dashboard" },
      { label: "Announcements", path: "/tutor/announcements", icon: <NotificationsIcon />, permission: "tutor-portal-announcements" },
      { label: "Awards", path: "/tutor/awards", icon: <EmojiEventsIcon />, permission: "tutor-portal-awards" },
      { label: "Certificates", path: "/tutor/certificates", icon: <PictureAsPdfIcon />, permission: "tutor-portal-certificates" },
    ],
    []
  );

  const filteredMenuItems = useMemo(() => {
    const relaxed = tutorPortalPermissions === null;
    const has = (p) => relaxed || tutorPortalPermissions.includes(p);
    return menuItems.filter((item) => !item.permission || has(item.permission));
  }, [menuItems, tutorPortalPermissions]);

  useEffect(() => {
    if (process.env.REACT_APP_DISABLE_TUTOR_PERMISSIONS_SYNC === "true") {
      setTutorPermissionsSynced(true);
      return;
    }
    if ((localStorage.getItem("role") || "") === "tester") {
      try {
        const raw = localStorage.getItem("tester-permissions");
        const parsed = raw ? JSON.parse(raw) : [];
        setTutorPortalPermissions(Array.isArray(parsed) ? parsed : []);
      } catch {
        setTutorPortalPermissions([]);
      }
      setTutorPermissionsSynced(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("tutor/auth/permissions", {
          validateStatus: (status) => status === 200 || status === 404,
        });
        if (cancelled) return;
        if (res.status === 404) {
          localStorage.removeItem("tutor-permissions");
          setTutorPortalPermissions(null);
          return;
        }
        const data = res.data;
        if (!data?.success || !Array.isArray(data.data?.permissions)) {
          localStorage.removeItem("tutor-permissions");
          setTutorPortalPermissions(null);
          return;
        }
        const perms = data.data.permissions;
        localStorage.setItem("tutor-permissions", JSON.stringify(perms));
        setTutorPortalPermissions(perms);
      } catch {
        if (!cancelled) {
          localStorage.removeItem("tutor-permissions");
          setTutorPortalPermissions(null);
        }
      } finally {
        if (!cancelled) {
          setTutorPermissionsSynced(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("role");
    localStorage.removeItem("token");
    localStorage.removeItem("tutor_name");
    localStorage.removeItem("tutor_email");
    localStorage.removeItem("tutor-permissions");
    window.location.href = "/tutor-login";
  };

  useEffect(() => {
    setTutorName(localStorage.getItem("tutor_name") || "");
    setTutorEmail(localStorage.getItem("tutor_email") || "");
  }, []);

  // Below md the sidebar is an overlay: closed by default, closed again after navigating.
  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleHamburger = () => {
    if (isMobile) {
      setMobileOpen((prev) => !prev);
    } else {
      setSidebarOpen((prev) => !prev);
    }
  };

  const isMenuActive = (item) => {
    const currentPath = location.pathname.replace(/\/$/, "") || "/tutor";
    return currentPath === item.path || (item.path === "/tutor" && currentPath === "/tutor");
  };

  const fetchAnnouncementUnreadCount = async () => {
    try {
      const response = await api.get("tutor/notifications/unread-announcement-count");
      if (response.data?.success && response.data?.data?.count != null) {
        setAnnouncementCount(Number(response.data.data.count) || 0);
      } else {
        setAnnouncementCount(0);
      }
    } catch {
      setAnnouncementCount(0);
    }
  };

  const fetchGeneralUnreadCount = async () => {
    try {
      const response = await api.get("tutor/notifications/unread-count");
      if (response.data?.success && response.data?.data?.count != null) {
        setNotificationCount(Number(response.data.data.count) || 0);
      } else {
        setNotificationCount(0);
      }
    } catch {
      setNotificationCount(0);
    }
  };

  // When user opens Announcements page, mark all announcement notifications as read so badge shows 0
  useEffect(() => {
    if (location.pathname.startsWith("/tutor/announcements")) {
      api.post("tutor/notifications/mark-all-announcements-read").then(() => {
        setAnnouncementCount(0);
      }).catch(() => {});
    } else {
      fetchAnnouncementUnreadCount();
    }

    if (!location.pathname.startsWith("/tutor/notifications")) {
      fetchGeneralUnreadCount();
    }
  }, [location.pathname]);

  const navList = (
    <List sx={{ pt: 2 }}>
      {filteredMenuItems.map((item) => (
        <ListItemButton
          key={item.label}
          data-tour={item.label}
          selected={isMenuActive(item)}
          sx={{
            mx: 1,
            borderRadius: 2,
            mb: 0.5,
            background: isMenuActive(item)
              ? "linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
              : "transparent",
            color: isMenuActive(item) ? "#fff" : "#495057",
            "&:hover": {
              background: isMenuActive(item)
                ? "linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
                : "rgba(102, 126, 234, 0.1)",
              transform: "translateX(4px)",
            },
            transition: "all 0.3s ease",
            "&.Mui-selected": {
              background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
              color: "#fff",
            },
          }}
          onClick={() => {
            navigate(item.path);
            setMobileOpen(false);
          }}
        >
          <ListItemIcon
            sx={{
              color: isMenuActive(item) ? "#fff" : "#667eea",
              minWidth: 40,
            }}
          >
            {item.label === "Announcements" ? (
              <Badge badgeContent={announcementCount} color="error" max={99} invisible={announcementCount === 0 || location.pathname.startsWith("/tutor/announcements")}>
                {item.icon}
              </Badge>
            ) : item.label === "Notifications" ? (
              <Badge badgeContent={notificationCount} color="error" max={99} invisible={notificationCount === 0 || location.pathname.startsWith("/tutor/notifications")}>
                {item.icon}
              </Badge>
            ) : (
              item.icon
            )}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            sx={{
              "& .MuiTypography-root": {
                fontWeight: isMenuActive(item) ? 600 : 500,
                fontSize: "14px",
              },
            }}
          />
        </ListItemButton>
      ))}
    </List>
  );

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      "@supports (height: 100dvh)": { height: "100dvh" },
      overflow: "hidden",
    }}>
      <CssBaseline />
      {loading && <LinearProgress sx={{ height: 4 }} />}
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden", minHeight: 0 }}>
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
            boxShadow: "none",
            top: loading ? "4px" : 0,
            transition: "top 0.3s",
          }}
        >
          <Toolbar sx={{
            justifyContent: "space-between",
            px: { xs: 1.5, sm: 3 },
            minHeight: { xs: 56, sm: 70 },
            height: { xs: 56, sm: 70 },
            background: "rgba(255,255,255,0.05)",
            backdropFilter: "blur(10px)",
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, minWidth: 0, flex: 1 }}>
              {/* Sidebar Toggle Button */}
              <Tooltip title={isMobile ? (mobileOpen ? "Close menu" : "Open menu") : (sidebarOpen ? "Hide Sidebar" : "Show Sidebar")} arrow>
                <IconButton
                  onClick={handleHamburger}
                  sx={{
                    background: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    borderRadius: "12px",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    "&:hover": {
                      background: "rgba(255,255,255,0.25)",
                      transform: "scale(1.05)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.3)"
                    },
                    transition: "all 0.3s ease"
                  }}
                >
                  <MenuIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>

              <Box
                sx={{
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "12px",
                  p: 1.5,
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}
              >
                <Typography sx={{ fontSize: "28px" }}>👨‍🏫</Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h5"
                  noWrap
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: { xs: "1rem", sm: "22px" },
                    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  }}
                >
                  Tutor Panel
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 2 }, flexShrink: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "50%",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AccountCircle sx={{ color: "#fff", fontSize: 28 }} />
                </Box>
                <Box sx={{ textAlign: "right", display: { xs: "none", sm: "block" } }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#fff", fontWeight: 600, fontSize: "14px" }}
                  >
                    {tutorName || "Tutor"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.7)", fontSize: "11px" }}
                  >
                    {tutorEmail}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleLogout}
                aria-label="Logout"
                sx={{
                  display: { xs: "inline-flex", sm: "none" },
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "12px",
                  "&:hover": { background: "rgba(255,255,255,0.25)" },
                }}
              >
                <Logout />
              </IconButton>
              <Button
                onClick={handleLogout}
                variant="contained"
                size="medium"
                startIcon={<Logout />}
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "12px",
                  px: 2.5,
                  py: 1,
                  fontWeight: 600,
                  fontSize: "13px",
                  textTransform: "none",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                  "&:hover": {
                    background: "rgba(255,255,255,0.25)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Logout
              </Button>
            </Box>
            <TesterPortalSwitcher />
          </Toolbar>
        </AppBar>

        {!isMobile && (
          <Drawer
            variant="permanent"
            open={sidebarOpen}
            sx={{
              width: sidebarOpen ? drawerWidth : 0,
              flexShrink: 0,
              transition: "width 0.3s ease",
              [`& .MuiDrawer-paper`]: {
                ...tutorDrawerPaperSx(sidebarOpen ? drawerWidth : 0, appBarHeight),
                boxShadow: sidebarOpen ? "2px 0 10px rgba(0,0,0,0.1)" : "none",
                overflowY: sidebarOpen ? "auto" : "hidden",
              },
            }}
          >
            {navList}
          </Drawer>
        )}
        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              [`& .MuiDrawer-paper`]: {
                ...tutorDrawerPaperSx(drawerWidth, appBarHeight),
                width: `min(${drawerWidth}px, 85vw)`,
                boxShadow: "4px 0 20px rgba(0,0,0,0.15)",
              },
            }}
          >
            {navList}
          </Drawer>
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minHeight: 0,
            minWidth: 0,
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            p: { xs: 1, sm: 2, md: 3 },
            pb: 8,
            mt: `${appBarHeight}px`,
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": { width: "10px" },
            "&::-webkit-scrollbar-track": {
              background: "rgba(0,0,0,0.05)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "10px",
              border: "2px solid #f9f9f9",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "linear-gradient(180deg, #3B2A9F 0%, #D62926 100%)",
            },
            scrollbarWidth: "thin",
            scrollbarColor: "#667eea rgba(0,0,0,0.05)",
          }}
        >
          <Box sx={{ pb: 4 }}>
            <Outlet context={{ tutorPermissionsSynced, tutorPortalPermissions }} />
          </Box>
        </Box>
        <AssistantDrawer role="tutor" />
        <ParentWelcomeTour
          portal="tutor"
          onRequestDrawer={isMobile ? setMobileOpen : undefined}
          userId={tourUser?.id}
          firstName={tourUser?.first_name || tourUser?.full_name?.split(" ")[0]}
        />
      </Box>
    </Box>
  );
};

export default TutorLayout;
